import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { DatadogService } from '@cudirect/sawdust/datadog';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { AppConfigService, JwtToken } from '@cudirect/shared-ui-library';
import { UserSessionService } from '@shared/service/user-session.service';
import { RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { EMPTY, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

/**
 * This class will intercept all Http Responses and add JWTs as needed
 */
@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  private logger = inject(SawDust);
  private datadog = inject(DatadogService);
  private readonly isBrowser: boolean;

  private logContext(additional: Record<string, unknown> = {}) {
    const base: Record<string, unknown> = { component: 'HeaderInterceptor' };
    if (typeof this.appNumber === 'number') {
      base.appNumber = this.appNumber;
    }
    return { ...base, ...additional };
  }

  constructor(
    private readonly statefulService: StatefulService,
    private readonly userSessionService: UserSessionService,
    private configService: AppConfigService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }
  public appNumber?: number;

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    let request: HttpRequest<unknown> = req;
    const cpapiRequest = req.url.includes('cpapi');
    const flowType = this.isBrowser ? (sessionStorage.getItem('BMP.FlowType') ?? 'OLA') : 'OLA';

    this.logger.debug('Intercepting HTTP request', this.logContext({ url: req.url, method: req.method, cpapiRequest }));

    // TODO: Remove header adding logic. This doesn't belong in the interceptor
    if (cpapiRequest) {
      // Adding this to avoid failed bundle save on page navigation to logout/login
      if (this.userSessionService.logoutInProgress) {
        this.logger.warn('Abort request during logout sequence', this.logContext({ url: req.url }));
        return EMPTY;
      }

      // Splitting the method to fix Sonar Gate Cognitive Complexity Issue
      this.setApplicationId(req, flowType);

      // Adds Header for DataDog RUM
      // Probably a better way to do this, but this is a working solution for now
      if (this.isBrowser && this.appNumber && this.appNumber > -1) {
        // appNumber needs to be a string in quotes in SessionStorage for it to work
        // We should refactor all gets/sets for appNumbers so it doesn't need this
        const id = JSON.stringify(this.appNumber.toString());
        sessionStorage.setItem('appNumber', id);
        this.logger.debug('Persisted application number to session storage', this.logContext());
      }

      let tokenStr;
      if (this.isBrowser) {
        if (this.configService.settings.arcDXTokenStorageType === 'sessionStorage') {
          tokenStr = sessionStorage.getItem('ecp-user-session');
        } else {
          tokenStr = localStorage.getItem('ecp-user-session');
        }
      }

      const authReq: HttpRequest<unknown> = req;

      // Leaving this here temporarily until we settle on a bundle guid method.
      // if (req.headers.has('Authorization')) {
      //   // const Authorization = JSON.parse(sessionStorage.getItem('cuASSD:ss'))?.authorization;
      //   // if (Authorization && Authorization !== req.headers.get('Authorization')) {
      //   //   console.log('Got one!');
      //   //   console.log('Headers have ' + req.headers.get('Authorization'));
      //   //   console.log('Session has ' + Authorization);
      //   //   console.log(JSON.stringify(req));
      //   // }

      //   // authReq = req.clone({
      //   //   setHeaders: {
      //   //     Authorization
      //   //   }
      //   // });
      // } else {
      //   authReq = req;
      // }
      const hasVersion = authReq?.headers?.has('x-api-version');
      if (tokenStr) {
        const token: JwtToken = JSON.parse(tokenStr);
        const header = {
          [RequestHeaderConst.cacheControlName]: 'no-cache',
          [RequestHeaderConst.pragma]: 'no-cache',
          Usertoken: token.access_token,
          AppNumber: this.appNumber ? String(this.appNumber) : '-1',
          FlowType: flowType,
          [RequestHeaderConst.origin]: this.isBrowser
            ? location.origin
            : this.statefulService.wrap('ss:appOriginatedURL').valueString?.split('?')[0] || 'http://localhost'
        };
        if (!hasVersion) {
          header['x-api-version'] = '1.0';
        }
        request = authReq.clone({
          setHeaders: header
        });
        this.logger.debug('Attached authorization headers to CPAPI request', this.logContext({ flowType, hasToken: true }));
      } else {
        const header = {
          [RequestHeaderConst.cacheControlName]: 'no-cache',
          [RequestHeaderConst.pragma]: 'no-cache',
          // TODO: This is only used for cpapi/api calls, and they are all on v1.
          // Should be moved to where the call is made, not added by the interceptor
          AppNumber: this.appNumber ? String(this.appNumber) : '-1',
          FlowType: flowType,
          // ssr relying on ss:appOriginatedURL being set by AppModule—which is currently only set when isBrowser
          [RequestHeaderConst.origin]: this.isBrowser
            ? location.origin
            : this.statefulService.wrap('ss:appOriginatedURL').valueString?.split('?')[0] ||
              this.configService?.settings?.appBaseUrl?.replace(/\/+$/, '') ||
              'http://localhost'
        };
        if (!hasVersion) {
          [RequestHeaderConst.apiVersion] = '1.0';
        }
        request = authReq.clone({
          setHeaders: header
        });
        this.logger.debug('Attached default headers to CPAPI request', this.logContext({ flowType, hasToken: false }));
      }
    } else {
      this.logger.debug('Forwarding non-CPAPI request without header mutation', this.logContext({ url: req.url }));
    }
    return next.handle(request).pipe(
      tap({
        next: () => {},
        error: (error: HttpErrorResponse) => {
          const errorCode = 401;
          if (error.status === errorCode && !req.url.includes('logout')) {
            // Added this check to prevent bundle calls from triggering logout attempts after a logout.
            if (this.userSessionService.token) {
              this.logger.warn('Received unauthorized response; triggering logout', this.logContext({ url: req.url }));
              this.userSessionService.logOut(true);
            }
          }
        },
        complete: () => {}
      })
    );
  }

  /**
   * Setting up the ApplicationId
   */
  private setApplicationId(req: HttpRequest<unknown>, flowType: string): void {
    // If there's a JWT token in storage, we're going to add it to the request headers
    if (!req.url.includes('appInit')) {
      // When we submit application, bundle will be erased and re-created uploan login call.
      // At this point, Reading applicationid from bundle becomes null.
      // So, Fetching the applicationId from session when we make a call from Postapp.

      const sessionAppId = this.isBrowser ? sessionStorage.getItem('appNumber') : null;

      // First check if we have a valid value
      if (sessionAppId) {
        try {
          const parsedAppId = JSON.parse(sessionAppId);

          // Check if it's a valid positive number or zero
          if (!isNaN(parsedAppId) && parsedAppId > 0) {
            this.appNumber = parsedAppId;
            this.logger.debug('Resolved application number from session storage', this.logContext({ source: 'sessionStorage' }));
            if (this.isBrowser) {
              this.datadog.setUserProperty('id', String(parsedAppId));
              this.datadog.setUserProperty('type', 'applicant');
            }
            return;
          }
        } catch (e) {
          this.logger.warn(
            'Failed to parse application number from session storage',
            this.logContext({ source: 'sessionStorage', valueLength: sessionAppId.length })
          );
        }
      }

      let ssAppId;
      if (flowType.includes('OLA')) {
        ssAppId = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueString;
      } else {
        ssAppId = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
      }

      if (ssAppId) {
        try {
          const parsedAppId = JSON.parse(ssAppId); // turn the string into a number
          // Check if it's a valid positive number or zero
          if (!isNaN(parsedAppId) && parsedAppId > 0) {
            this.appNumber = parsedAppId;
            this.logger.debug('Resolved application number from stateful storage', this.logContext({ source: 'statefulService' }));
            // Make these props sticky with every log going forward.
            this.logger.withContext({ appNumber: parsedAppId });
            if (this.isBrowser) {
              this.datadog.setUserProperty('id', String(parsedAppId));
              this.datadog.setUserProperty('type', 'applicant');
            }
            return;
          }
        } catch (e) {
          this.logger.warn(
            'Failed to parse application number from stateful storage',
            this.logContext({ source: 'statefulService', valueLength: ssAppId.length })
          );
        }
      }
    }
  }
}
